import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert,
  Link,
  CircularProgress,
} from '@mui/material';
import {
  Warning as WarningIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { interventionAPI } from '../../services/api';

/**
 * InterventionDialog Component
 * 
 * Displays a dialog when Browserless.io requires manual intervention
 * (e.g., Google OAuth login via LiveURL).
 * 
 * Features:
 * - Polls for pending interventions during enrichment
 * - Countdown timer showing time until LiveURL expires
 * - Opens LiveURL in new tab for user to complete verification
 * - "I've Completed Verification" button to mark as done
 * 
 * Usage:
 *   <InterventionDialog 
 *     jobId={4} 
 *     leadId={123} 
 *     open={isEnriching}
 *     onComplete={() => refetchLead()}
 *   />
 */
const InterventionDialog = ({ 
  jobId, 
  leadId, 
  open = false,
  pollingInterval = 5000,  // 5 seconds
  onComplete,
  onCancel,
}) => {
  const queryClient = useQueryClient();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [liveUrlOpened, setLiveUrlOpened] = useState(false);

  // Poll for pending intervention
  const { 
    data: interventionData, 
    isLoading,
    error,
  } = useQuery(
    ['intervention', jobId, leadId],
    () => interventionAPI.checkPending(jobId, leadId),
    {
      enabled: open && !!jobId && !!leadId,
      refetchInterval: (data) => {
        // Stop polling if no pending intervention
        if (!data?.pending) return false;
        return pollingInterval;
      },
      onSuccess: (data) => {
        if (data?.pending && data?.intervention?.expires_at) {
          // Calculate initial time remaining
          const expiresAt = new Date(data.intervention.expires_at);
          const now = new Date();
          const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
          setTimeRemaining(remaining);
        }
      }
    }
  );

  // Mutation to mark intervention complete
  const completeMutation = useMutation(
    (interventionId) => interventionAPI.markComplete(interventionId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['intervention', jobId, leadId]);
        onComplete?.();
      },
    }
  );

  // Mutation to cancel intervention
  const cancelMutation = useMutation(
    (interventionId) => interventionAPI.cancel(interventionId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['intervention', jobId, leadId]);
        onCancel?.();
      },
    }
  );

  // Countdown timer
  useEffect(() => {
    if (timeRemaining === null || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  // Format time remaining as MM:SS
  const formatTime = useCallback((seconds) => {
    if (seconds === null) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Handle opening LiveURL
  const handleOpenLiveUrl = useCallback(() => {
    if (interventionData?.intervention?.live_url) {
      window.open(interventionData.intervention.live_url, '_blank');
      setLiveUrlOpened(true);
    }
  }, [interventionData?.intervention?.live_url]);

  // Handle marking as complete
  const handleComplete = useCallback(() => {
    if (interventionData?.intervention?.id) {
      completeMutation.mutate(interventionData.intervention.id);
    }
  }, [interventionData?.intervention?.id, completeMutation]);

  // Handle cancel/skip
  const handleCancel = useCallback(() => {
    if (interventionData?.intervention?.id) {
      cancelMutation.mutate(interventionData.intervention.id);
    }
  }, [interventionData?.intervention?.id, cancelMutation]);

  // Don't show dialog if no pending intervention
  const hasPendingIntervention = interventionData?.pending && interventionData?.intervention;

  if (!open || isLoading) {
    return null;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to check intervention status: {error.message}
      </Alert>
    );
  }

  if (!hasPendingIntervention) {
    return null;
  }

  const intervention = interventionData.intervention;
  const isExpired = timeRemaining === 0;
  const isProcessing = completeMutation.isLoading || cancelMutation.isLoading;

  return (
    <Dialog 
      open={true} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="warning" />
        <Typography variant="h6" component="span">
          Manual Verification Required
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* Explanation */}
        <Alert severity="info" sx={{ mb: 3 }}>
          {intervention.message || 'Cloudflare or Google requires manual verification to continue.'}
        </Alert>

        {/* Timer */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 1,
            mb: 3,
            p: 2,
            bgcolor: isExpired ? 'error.light' : 'grey.100',
            borderRadius: 1,
          }}
        >
          <TimerIcon color={isExpired ? 'error' : 'action'} />
          <Typography 
            variant="h4" 
            sx={{ 
              fontFamily: 'monospace',
              color: isExpired ? 'error.dark' : 'text.primary',
            }}
          >
            {formatTime(timeRemaining)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            remaining
          </Typography>
        </Box>

        {isExpired && (
          <Alert severity="error" sx={{ mb: 3 }}>
            LiveURL has expired. Please cancel and retry the enrichment.
          </Alert>
        )}

        {/* Progress bar */}
        {!isExpired && timeRemaining !== null && (
          <LinearProgress 
            variant="determinate" 
            value={(timeRemaining / 300) * 100}  // 5 min = 300 sec
            sx={{ mb: 3, height: 8, borderRadius: 1 }}
            color={timeRemaining < 60 ? 'warning' : 'primary'}
          />
        )}

        {/* Instructions */}
        <Typography variant="body1" paragraph>
          <strong>Steps to complete verification:</strong>
        </Typography>
        <Box component="ol" sx={{ pl: 2, mb: 3 }}>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Click the button below to open the verification page
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Click "Sign in with Google" on RecordOwl
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Enter your Google email and password (complete 2FA if prompted)
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              After login succeeds, you'll see the Google Workspace page
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Close the browser tab</strong> - this signals completion to the system
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Click "I've Completed Verification" below to update the status
            </Typography>
          </li>
        </Box>

        {/* Open LiveURL button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenLiveUrl}
            disabled={isExpired || isProcessing}
          >
            Open Verification Page
          </Button>
        </Box>

        {liveUrlOpened && !isExpired && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
            Verification page opened! Complete the login, then click below.
          </Alert>
        )}

        {/* Direct URL link (fallback) */}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Or copy the URL:{' '}
            <Link 
              href={intervention.live_url} 
              target="_blank"
              rel="noopener noreferrer"
              sx={{ wordBreak: 'break-all' }}
            >
              {intervention.live_url.substring(0, 60)}...
            </Link>
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button 
          onClick={handleCancel}
          color="inherit"
          disabled={isProcessing}
        >
          Skip & Continue
        </Button>
        
        <Button
          variant="contained"
          color="success"
          onClick={handleComplete}
          disabled={!liveUrlOpened || isExpired || isProcessing}
          startIcon={isProcessing ? <CircularProgress size={16} /> : <CheckCircleIcon />}
        >
          {isProcessing ? 'Processing...' : "I've Completed Verification"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InterventionDialog;
